import { Login } from '@lucid/auth';
import { LOGIN_LABELS_ID } from '../../config/constants';
import { useStore } from '../../store/store';
import { useLabels } from '../../hooks/useLabels';
import { convertToContentfulLocale } from '../../utils/tools';

/**
 * LoginPage
 *
 * @component
 * App-level wrapper around the `@lucid/auth` `Login` component that supplies
 * internationalized copy. It fetches localized labels from Contentful for the
 * active locale/language and forwards each message to `Login`. When a label is
 * missing (or the Contentful entry has not been authored yet) the English
 * defaults are used as a fallback, keeping the page i18n-ready without breaking.
 *
 * Supported languages: en, de, fr, nl, nb, ar (resolved via the store locale).
 *
 * @returns The localized login page.
 *
 * @example
 * ```tsx
 * <Route path="/login" element={<LoginPage />} />
 * ```
 */
const LoginPage = () => {
  const locale = useStore((state) => state.locale);

  const { data: labels } = useLabels({
    id: LOGIN_LABELS_ID,
    locale: convertToContentfulLocale(locale),
    enabled: Boolean(LOGIN_LABELS_ID),
  });

  return (
    <Login
      title={labels?.['login.title'] ?? 'Partner Portal'}
      subtitle={labels?.['login.subtitle'] ?? 'Sign in to access your account'}
      logoutMessage={
        labels?.['login.logoutMessage'] ??
        'You have successfully logged out. Please use the login button below to login.'
      }
      sessionExpiredMessage={
        labels?.['login.sessionExpiredMessage'] ??
        'Your session has expired. Please log in again to continue.'
      }
      authErrorMessage={
        labels?.['login.authErrorMessage'] ?? 'Unable to verify your account. Please log in again.'
      }
      identityErrorMessage={
        labels?.['login.identityErrorMessage'] ??
        "We couldn't load your account. Please try again, or contact support if the problem persists."
      }
      loginButtonLabel={labels?.['login.buttonLabel'] ?? 'Login'}
    />
  );
};

export default LoginPage;
